import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import { useAuth } from '@/modules/Auth/useAuth'

import { usePlanStore } from './usePlanStore'
import { Resource } from './planTypes'
import { getResourceLimit, getResourceUsage } from './limitAndUsage'

/**
 * Intended to be the single source of truth around usage and limits for
 * the 'workspace' LBB resource
 *
 * Should be extended with usage checks, limits and any other logic that might be
 * needed across the app.
 */
export const useResourceWorkspace = (): {
  isWorkspaceAtLimit: ComputedRef<boolean>
} => {
  const { isAuthorized } = useAuth()
  const planStore = usePlanStore()

  const isWorkspaceAtLimit = computed(() => {
    // if we are on an LBB plan, then we check the plan and nothing else
    const plan = planStore.customerPlan
    if (plan) {
      // NOTE: Classic LBB actually did a soft-block here,
      // even though the plan resource might not be blocking, so wit this new code, we do the same
      const license = plan?.plan_licenses?.find((l) => l.resource === Resource.WORKSPACE)
      const limit = getResourceLimit(plan, Resource.WORKSPACE)
      const usage = getResourceUsage(plan, Resource.WORKSPACE)
      return license ? usage >= limit : false
    }

    // legacy code taken from the original Sidebar.vue before we introduced self-serve
    // it's possible we could be checking `team.managed_status === 'partner'` to same effect here
    // what we're missing is checking for `team.managd_meta.max_client_invites`
    if (isAuthorized('manage_client_team')) {
      return false
    }

    // Legacy CBB customers do not have a concept of additional workspaces,
    // so they are at limit by default
    return true
  })

  return {
    isWorkspaceAtLimit,
  }
}
